import { User } from 'lib-shared-types-api';

import { ratingApi } from 'apis/rating';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type ReturnType = {
  toggleExclusion: () => void;
  isAlreadyExcluded: boolean;
  isLoadingUserExclusion: boolean;
};

/**
 * Hook to handle the exclusion of a contact from CSAT surveys. Can be used
 * anywhere a quick csat-exclusion toggle action is needed such as dropdowns.
 *
 * @param user - The user to exclude from CSAT surveys.
 */
export function useExcludeContactFromCsatSurvey(user: User): ReturnType {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const [deleteUserExclusionById] = ratingApi.useDeleteUserExclusionMutation();
  const [createUserExclusion] = ratingApi.useCreateUserExclusionMutation();

  const {
    refetch,
    data: userExclusionData,
    isError,
    isFetching: isLoadingUserExclusion,
  } = ratingApi.useReadUserExclusionByIdQuery(
    { userId: user.id },
    { refetchOnMountOrArgChange: true },
  );

  // If the exclusion record is found and there are no errors, the user is excluded from CSAT surveys
  const isAlreadyExcluded = Boolean(userExclusionData?.data && !isError);

  const openConfirmExclusionModal = async () => {
    const result = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Exclude from CSAT surveys',
            description:
              'Are you sure you want to exclude this user from CSAT surveys?',
            confirm: { variant: 'danger', label: 'Exclude' },
          },
        },
      }),
    );

    if (result.isConfirmed) {
      try {
        await createUserExclusion({ userId: user.id });
        notify(
          {
            time: 5000,
            type: 'success',
            title: 'Success',
            text: 'User excluded from CSAT surveys',
          },
          NotificationType.Success,
        );
        refetch();
      } catch {
        notify(
          {
            time: 5000,
            type: 'error',
            title: 'Error',
            text: 'Failed to exclude user from CSAT surveys',
          },
          NotificationType.Error,
        );
      }
    }
  };

  const openConfirmRemoveExclusionModal = async () => {
    const result = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Remove exclusion from CSAT surveys',
            description:
              'Are you sure you want to remove the user CSAT exclusion?',
            confirm: { variant: 'primary', label: 'Remove' },
          },
        },
      }),
    );

    if (result.isConfirmed) {
      try {
        await deleteUserExclusionById({
          userId: user.id,
        });
        notify(
          {
            time: 5000,
            type: 'success',
            title: 'Success',
            text: 'User CSAT exclusion removed',
          },
          NotificationType.Success,
        );
        refetch();
      } catch {
        notify(
          {
            time: 5000,
            type: 'error',
            title: 'Error',
            text: 'Failed to remove CSAT exclusion',
          },
          NotificationType.Error,
        );
      }
    }
  };

  const toggleExclusion = () => {
    if (isAlreadyExcluded) {
      openConfirmRemoveExclusionModal();
    } else {
      openConfirmExclusionModal();
    }
  };

  return {
    toggleExclusion,
    isAlreadyExcluded,
    isLoadingUserExclusion,
  };
}
